import React, { useState, useRef, useEffect } from "react";
import firebase from "firebase";
import { uploadMedia } from "../../../database/media/cloudflareMedia";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Switch from "@material-ui/core/Switch";
import IconButton from "@material-ui/core/IconButton";
import ArrowBack from "@material-ui/icons/ArrowBack";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CloseIcon from "@material-ui/icons/Close";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import styled from "styled-components";
import Input from "../../common/CustomInput";
import {
  createCommunityAgent,
  createInstantVoiceClone,
  pickDefaultAgentVoice,
  DEFAULT_AGENT_VOICES,
  describeError,
  generateFullCommunityContent,
  editDraftWithChat,
  GeneratedDraft,
  FullCommunityContent,
  StarterContent,
  UploadedFile,
  COMMUNITY_CATEGORIES,
} from "./OnboardingAPI";
import {
  useOnboardingDraft,
  snapshotHasContent,
  OnboardingDraftSnapshot,
} from "./useOnboardingDraft";

// ─── Design tokens (bridged to design-system CSS variables) ──────────────────

const T = {
  text: "var(--color-text)",
  textMid: "var(--color-text-secondary)",
  textLow: "var(--color-text-tertiary)",
  textVeryLow: "var(--color-text-tertiary)",
  border: "var(--color-border)",
  borderHover: "var(--color-border-strong)",
  borderFocus: "var(--color-accent)",
  surface: "var(--color-surface)",
  surfaceHover: "var(--color-surface-subtle)",
  surfaceActive: "var(--color-surface-inset)",
  accent: "var(--color-accent)",
  accentBg: "var(--color-accent-subtle)",
  green: "var(--color-success)",
  greenBg: "var(--color-success-subtle)",
  greenBorder: "var(--color-success-subtle)",
};

// ─── Styled Components ────────────────────────────────────────────────────────

const Wizard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`;

const StepBar = styled.div`
  display: flex;
  gap: 6px;
`;

const StepSegment = styled.div<{ active: boolean }>`
  flex: 1;
  height: 3px;
  border-radius: 2px;
  background: ${({ active }) => (active ? T.accent : T.border)};
  transition: background 0.3s;
`;

const Header = styled.div`
  margin-bottom: 4px;
`;

const SectionNav = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 2px;
`;

const BackBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px;
  margin-left: -6px;
  color: ${T.textMid};
  display: flex;
  align-items: center;
  border-radius: 50%;
  transition: background 0.15s;
  &:hover { background: ${T.surfaceHover}; }
  &:focus { outline: none; }
`;

const LogoutBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 10px;
  margin-left: auto;
  color: ${T.textLow};
  font-size: 12px;
  border-radius: 6px;
  transition: background 0.15s, color 0.15s;
  &:hover { background: ${T.surfaceHover}; color: ${T.textMid}; }
  &:focus { outline: none; }
`;

const Title = styled.h2`
  margin: 0 0 6px 0;
  font-size: 22px;
  font-weight: 700;
  color: ${T.text};
  /* Focused programmatically on step change for screen readers, not a
     keyboard target, so no visible focus ring. */
  &:focus { outline: none; }
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${T.textLow};
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 90px;
  padding: 12px 14px;
  border: 1.5px solid ${T.border};
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  color: ${T.text};
  background: ${T.surface};
  resize: vertical;
  box-sizing: border-box;
  transition: border-color 0.2s;
  &:focus {
    outline: none;
    border-color: ${T.borderFocus};
  }
  &::placeholder { color: ${T.textVeryLow}; }
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: ${T.textMid};
  margin-bottom: 6px;
`;

const ButtonRow = styled.div`
  width: 100%;
  margin-top: 4px;
`;

const ChipGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Chip = styled.button<{ selected: boolean }>`
  padding: 7px 14px;
  border-radius: 20px;
  border: 1.5px solid ${({ selected }) => (selected ? T.accent : T.border)};
  background: ${({ selected }) => (selected ? T.accentBg : "transparent")};
  color: ${({ selected }) => (selected ? T.text : T.textLow)};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  &:hover {
    border-color: ${T.borderHover};
    color: ${T.textMid};
  }
  &:focus { outline: none; }
`;

/* Segmented source selector (Link / Paste / Upload) so step 0 shows one
   input at a time instead of a long form. */
const SourceTabBar = styled.div`
  display: flex;
  border: 1.5px solid ${T.border};
  border-radius: 10px;
  overflow: hidden;
`;

const SourceTab = styled.button<{ active: boolean }>`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 12px;
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  border: none;
  background: ${({ active }) => (active ? T.surfaceActive : "transparent")};
  color: ${({ active }) => (active ? T.text : T.textLow)};
  transition: background 0.15s, color 0.15s;
  &:hover { background: ${T.surfaceHover}; color: ${T.textMid}; }
`;

const SourceTabDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${T.green};
  flex-shrink: 0;
`;

/* Inline notice used for the resume-draft offer and the back confirmation. */
const NoticeBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 10px 14px;
  border: 1px solid ${T.border};
  border-radius: 10px;
  background: ${T.surface};
  font-size: 13px;
  line-height: 1.5;
  color: ${T.textMid};
`;

const NoticeActions = styled.div`
  display: flex;
  gap: 8px;
  margin-left: auto;
  flex-shrink: 0;
`;

const SmallBtn = styled.button<{ tone?: "primary" | "danger" }>`
  padding: 6px 14px;
  border-radius: 8px;
  font-size: 12.5px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.15s;
  border: 1.5px solid
    ${({ tone }) =>
      tone === "primary"
        ? "transparent"
        : tone === "danger"
        ? "var(--color-danger)"
        : T.border};
  background: ${({ tone }) => (tone === "primary" ? T.accent : "transparent")};
  color: ${({ tone }) =>
    tone === "primary"
      ? "var(--color-text-inverse)"
      : tone === "danger"
      ? "var(--color-danger)"
      : T.textMid};
  &:hover {
    ${({ tone }) =>
      tone === "primary"
        ? "filter: brightness(1.08);"
        : tone === "danger"
        ? "background: var(--color-danger-subtle);"
        : `border-color: ${T.borderHover}; color: ${T.text};`}
  }
`;

/* One-click launch callout pinned at the top of the review step. */
const QuickLaunchBar = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  padding: 14px 16px;
  border: 1px solid ${T.accent};
  border-radius: 12px;
  background: ${T.accentBg};
`;

/* Success screen actions: live page link + dashboard hand-off. */
const SuccessActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  max-width: 320px;
`;

const SeePageLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 48px;
  border-radius: 10px;
  background: ${T.accent};
  font-size: 15px;
  font-weight: 600;
  && {
    color: var(--color-text-inverse);
    text-decoration: none;
  }
  &:hover { filter: brightness(1.06); }
`;

const DashboardBtn = styled.button`
  height: 44px;
  border-radius: 10px;
  border: 1.5px solid ${T.border};
  background: transparent;
  color: ${T.textMid};
  font-size: 14px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { border-color: ${T.borderHover}; color: ${T.text}; }
`;

const UploadZone = styled.label`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 2px dashed ${T.border};
  border-radius: 10px;
  padding: 24px 16px;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
  color: ${T.textLow};
  font-size: 13px;
  text-align: center;
  &:hover {
    border-color: ${T.borderHover};
    background: ${T.surfaceHover};
    color: ${T.textMid};
  }
`;

const FileList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 8px;
`;

const FileItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  background: ${T.surface};
  border-radius: 6px;
  font-size: 13px;
  color: ${T.textMid};
`;

const HelperText = styled.div`
  margin-top: 8px;
  font-size: 12px;
  line-height: 1.5;
  color: ${T.textVeryLow};
`;

const PhotoRow = styled.div`
  display: flex;
  gap: 16px;
`;

const PhotoUploadBox = styled.label`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex: 1;
  border: 2px dashed ${T.border};
  border-radius: 10px;
  padding: 16px 10px;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
  color: ${T.textLow};
  font-size: 12px;
  text-align: center;
  min-height: 90px;
  &:hover {
    border-color: ${T.borderHover};
    background: ${T.surfaceHover};
    color: ${T.textMid};
  }
`;

const PhotoPreview = styled.img`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid ${T.border};
`;

const LogoPreview = styled.img`
  width: 60px;
  height: 60px;
  border-radius: 8px;
  object-fit: cover;
  border: 2px solid ${T.border};
`;

const VoiceTabBar = styled.div`
  display: flex;
  border: 1.5px solid ${T.border};
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 10px;
`;

const VoiceTab = styled.button<{ active: boolean }>`
  flex: 1;
  padding: 8px 10px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  background: ${({ active }) => (active ? T.surfaceActive : "transparent")};
  color: ${({ active }) => (active ? T.text : T.textLow)};
  transition: background 0.15s, color 0.15s;
  &:focus { outline: none; }
  &:hover { background: ${T.surfaceHover}; color: ${T.textMid}; }
`;

const VoiceOptionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
`;

const VoiceOption = styled.button<{ selected: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  padding: 9px 12px;
  border-radius: 8px;
  border: 1.5px solid ${({ selected }) => (selected ? T.accent : T.border)};
  background: ${({ selected }) => (selected ? T.accentBg : "transparent")};
  cursor: pointer;
  text-align: left;
  transition: all 0.15s;
  &:hover { border-color: ${T.borderHover}; }
  &:focus { outline: none; }
`;

const VoiceOptionName = styled.span<{ selected: boolean }>`
  font-size: 13px;
  font-weight: 600;
  color: ${({ selected }) => (selected ? T.text : T.textMid)};
`;

const VoiceOptionMeta = styled.span`
  font-size: 11px;
  color: ${T.textVeryLow};
  text-transform: capitalize;
`;

const RecordBtn = styled.button<{ recording: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 14px;
  border: 2px dashed ${({ recording }) => (recording ? "rgba(255,90,96,0.5)" : T.border)};
  border-radius: 10px;
  background: ${({ recording }) => (recording ? "rgba(255,90,96,0.06)" : "transparent")};
  color: ${({ recording }) => (recording ? "#ff5a60" : T.textLow)};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  &:focus { outline: none; }
  &:hover {
    border-color: ${({ recording }) => (recording ? "rgba(255,90,96,0.7)" : T.borderHover)};
    background: ${({ recording }) => (recording ? "rgba(255,90,96,0.1)" : T.surfaceHover)};
    color: ${({ recording }) => (recording ? "#ff5a60" : T.textMid)};
  }
`;

const RecordDot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #ff5a60;
  display: inline-block;
  animation: pulse 1s ease-in-out infinite;
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.3; }
  }
`;


const ColorRow = styled.div`
  display: flex;
  gap: 12px;
`;

const ColorField = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
`;

const ColorSwatch = styled.input`
  -webkit-appearance: none;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  padding: 0;
  cursor: pointer;
  background: none;
  &::-webkit-color-swatch-wrapper { padding: 0; }
  &::-webkit-color-swatch { border: none; border-radius: 8px; }
`;

const ColorHexInput = styled.input`
  flex: 1;
  padding: 6px 8px;
  border: 1.5px solid ${T.border};
  border-radius: 7px;
  font-size: 13px;
  font-family: inherit;
  color: ${T.text};
  background: ${T.surface};
  box-sizing: border-box;
  transition: border-color 0.2s;
  &:focus { outline: none; border-color: ${T.borderFocus}; }
`;

/* Chat editor */
const ChatSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  border-top: 1px solid ${T.border};
  padding-top: 12px;
`;

const ChatThread = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 180px;
  overflow-y: auto;
  padding-right: 2px;
  &::-webkit-scrollbar { width: 3px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: ${T.border}; border-radius: 2px; }
`;

const ChatBubble = styled.div<{ role: "user" | "assistant" }>`
  align-self: ${({ role }) => (role === "user" ? "flex-end" : "flex-start")};
  max-width: 85%;
  padding: 7px 11px;
  border-radius: ${({ role }) => (role === "user" ? "12px 12px 2px 12px" : "12px 12px 12px 2px")};
  background: ${({ role }) => (role === "user" ? T.surfaceActive : T.surface)};
  border: 1px solid ${({ role }) => (role === "user" ? T.border : "transparent")};
  font-size: 13px;
  color: ${({ role }) => (role === "user" ? T.text : T.textMid)};
  line-height: 1.5;
`;

const ChatInputRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-end;
`;

const ChatTextarea = styled.textarea`
  flex: 1;
  padding: 9px 12px;
  border: 1.5px solid ${T.border};
  border-radius: 10px;
  font-size: 13px;
  font-family: inherit;
  color: ${T.text};
  background: ${T.surface};
  resize: none;
  min-height: 40px;
  max-height: 100px;
  box-sizing: border-box;
  transition: border-color 0.2s;
  &:focus { outline: none; border-color: ${T.borderFocus}; }
  &::placeholder { color: ${T.textVeryLow}; }
`;

const ChatSendBtn = styled.button<{ disabled: boolean }>`
  flex-shrink: 0;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: none;
  background: ${({ disabled }) => (disabled ? T.border : T.accent)};
  color: ${({ disabled }) => (disabled ? T.textVeryLow : "#000")};
  cursor: ${({ disabled }) => (disabled ? "default" : "pointer")};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  transition: background 0.15s;
  &:focus { outline: none; }
`;

const GeneratingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 48px 0;
  text-align: center;
`;

const GeneratingMsg = styled.span`
  font-size: 14px;
  color: ${T.textLow};
  min-height: 20px;
`;

const LaunchingSteps = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  max-width: 300px;
  margin: 4px auto 0;
  text-align: left;
`;

const LaunchStepRow = styled.div<{ status: "done" | "active" | "pending" }>`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  font-weight: ${({ status }) => (status === "active" ? 600 : 400)};
  color: ${({ status }) =>
    status === "pending" ? T.textVeryLow : status === "active" ? T.text : T.textMid};
  transition: color 0.3s, font-weight 0.3s;
`;

const LaunchStepIcon = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const PendingDot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 2px solid ${T.border};
  box-sizing: border-box;
`;

const SuccessCheck = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  animation: successPop 0.4s ease-out;
  @keyframes successPop {
    0% { transform: scale(0.6); opacity: 0; }
    60% { transform: scale(1.1); }
    100% { transform: scale(1); opacity: 1; }
  }
`;

const ErrorMsg = styled.p`
  margin: 0;
  font-size: 13px;
  color: var(--color-danger);
  text-align: center;
`;

const ErrorBanner = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  padding: 10px 12px;
  margin-bottom: 12px;
  border-radius: 8px;
  background: var(--color-danger-subtle);
  border: 1px solid rgba(255, 90, 96, 0.25);
  font-size: 13px;
  line-height: 1.5;
  color: var(--color-danger);
`;

const CtaHint = styled.div<{ tone: "ok" | "muted" }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 10px;
  font-size: 12.5px;
  line-height: 1.5;
  text-align: center;
  color: ${({ tone }) => (tone === "ok" ? T.green : T.textVeryLow)};
`;

// ─── Review Step Styles ───────────────────────────────────────────────────────

const ReviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-height: 68vh;
  overflow-y: auto;
  padding-right: 4px;

  /* subtle scrollbar */
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: ${T.border}; border-radius: 2px; }
`;

const ReviewSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SectionHeader = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  &:focus { outline: none; }
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 700;
  color: ${T.text};
`;

const SectionCount = styled.span`
  font-size: 12px;
  font-weight: 500;
  padding: 2px 10px;
  border-radius: 12px;
  background: ${T.accentBg};
  color: ${T.textMid};
`;

const Divider = styled.div`
  height: 1px;
  background: ${T.border};
`;

const EmptyState = styled.div`
  font-size: 13px;
  line-height: 1.6;
  color: ${T.textVeryLow};
  padding: 10px 12px;
  border: 1px dashed ${T.border};
  border-radius: 8px;
  background: ${T.surface};
`;

/* Community profile inline fields */
const CommunityNameInput = styled.input`
  width: 100%;
  font-size: 22px;
  font-weight: 700;
  color: ${T.text};
  border: none;
  border-bottom: 2px solid transparent;
  padding: 2px 0 4px;
  background: transparent;
  font-family: inherit;
  box-sizing: border-box;
  transition: border-color 0.2s;
  &::placeholder { color: ${T.textVeryLow}; }
  &:hover { border-bottom-color: ${T.borderHover}; }
  &:focus {
    outline: none;
    border-bottom-color: ${T.borderFocus};
  }
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const InlineLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: ${T.textVeryLow};
  text-transform: uppercase;
  letter-spacing: 0.8px;
`;

const InlineTextArea = styled.textarea`
  width: 100%;
  font-size: 14px;
  line-height: 1.7;
  color: ${T.text};
  border: none;
  border-bottom: 1.5px solid transparent;
  padding: 2px 0;
  background: transparent;
  font-family: inherit;
  resize: none;
  box-sizing: border-box;
  overflow: hidden;
  transition: border-color 0.2s;
  &::placeholder { color: ${T.textVeryLow}; }
  &:hover { border-bottom-color: ${T.borderHover}; }
  &:focus {
    outline: none;
    border-bottom-color: ${T.borderFocus};
  }
`;

const InlineInput = styled.input`
  width: 100%;
  font-size: 14px;
  color: ${T.text};
  border: none;
  border-bottom: 1.5px solid transparent;
  padding: 2px 0;
  background: transparent;
  font-family: inherit;
  box-sizing: border-box;
  transition: border-color 0.2s;
  &::placeholder { color: ${T.textVeryLow}; }
  &:hover { border-bottom-color: ${T.borderHover}; }
  &:focus {
    outline: none;
    border-bottom-color: ${T.borderFocus};
  }
`;

const AdvancedToggle = styled.button`
  background: none;
  border: none;
  padding: 4px 0;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: ${T.textLow};
  display: flex;
  align-items: center;
  gap: 4px;
  transition: color 0.15s;
  &:focus { outline: none; }
  &:hover { color: ${T.textMid}; }
`;

/* Habit card */
const HabitCard = styled.div<{ enabled: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  border: 1.5px solid ${({ enabled }) => (enabled ? T.greenBorder : T.border)};
  border-radius: 10px;
  background: ${({ enabled }) => (enabled ? T.greenBg : T.surface)};
  opacity: ${({ enabled }) => (enabled ? 1 : 0.45)};
  transition: all 0.2s;
`;

const HabitInfo = styled.div`
  flex: 1;
`;

const HabitName = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${T.text};
  margin-bottom: 4px;
`;

const HabitDesc = styled.div`
  font-size: 13px;
  color: ${T.textMid};
  line-height: 1.6;
`;

const FreqBadge = styled.span`
  display: inline-block;
  margin-top: 6px;
  padding: 3px 10px;
  border-radius: 12px;
  background: ${T.accentBg};
  color: ${T.textLow};
  font-size: 12px;
  font-weight: 500;
`;

/* Plan */
const PlanHeader = styled.div`
  padding: 16px;
  background: ${T.surface};
  border: 1.5px solid ${T.border};
  border-radius: 10px 10px 0 0;
`;

const PlanTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${T.text};
  margin-bottom: 4px;
`;

const PlanDesc = styled.div`
  font-size: 13px;
  color: ${T.textMid};
  line-height: 1.6;
`;

const PlanDayList = styled.div`
  border: 1.5px solid ${T.border};
  border-top: none;
  border-radius: 0 0 10px 10px;
  overflow: hidden;
`;

const PlanDayRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid ${T.border};
  &:last-child { border-bottom: none; }
`;

const DayNumber = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: ${T.textLow};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  min-width: 40px;
  padding-top: 2px;
`;

const DayContent = styled.div`
  flex: 1;
`;

const DayTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${T.text};
  margin-bottom: 2px;
`;

const DaySteps = styled.div`
  font-size: 12px;
  color: ${T.textLow};
  line-height: 1.5;
`;

/* Quote card */
const QuoteCard = styled.div`
  padding: 14px 16px;
  border: 1.5px solid ${T.border};
  border-radius: 10px;
  background: ${T.surface};
  display: flex;
  gap: 10px;
  align-items: flex-start;
`;

const QuoteContent = styled.div`
  flex: 1;
`;

const QuoteText = styled.div`
  font-size: 14px;
  color: ${T.text};
  line-height: 1.7;
  font-style: italic;
  margin-bottom: 4px;
`;

const QuoteAuthor = styled.div`
  font-size: 12px;
  color: ${T.textLow};
  font-style: normal;
`;

// ─── Constants ────────────────────────────────────────────────────────────────

const STEP_META = [
  {
    title: "Let's build your community",
    subtitle: "Drop a link or paste your work. The agent builds the rest.",
  },
  {
    title: "Building your community…",
    subtitle: "Give the agent a few seconds.",
  },
  {
    title: "Here's what the agent built",
    subtitle: "Review, tweak anything, then launch when you're ready.",
  },
];

const LOADING_MESSAGES = [
  "Reading your content...",
  "Learning your voice...",
  "Shaping your community profile...",
  "Building your habits and first plan...",
  "Adding quotes in your tone...",
  "Almost ready...",
];

const AUDIO_SAMPLE_EXTENSION_REGEX = /\.(mp3|wav|m4a|aac|ogg|flac|webm)$/i;

const SOURCE_TABS = [
  { key: "link", label: "Link" },
  { key: "paste", label: "Paste" },
  { key: "upload", label: "Upload" },
] as const;

type SourceTabKey = (typeof SOURCE_TABS)[number]["key"];

// ─── Types ────────────────────────────────────────────────────────────────────

type Props = {
  userName: string;
  // Keys the local draft autosave per user. Optional so the wizard still
  // works (without autosave isolation) if a caller omits it.
  userId?: string;
  // May resolve with the created community slug (or id) so the wizard can
  // link to the live landing page at tribed.io/c/{slug} on success.
  onComplete: (
    draft: GeneratedDraft,
    content?: StarterContent
  ) => Promise<string | void>;
  // Called after the success screen has been shown, to hand off to the
  // dashboard. Optional so the wizard still works if a caller omits it.
  onFinished?: () => void;
};

// ─── Component ────────────────────────────────────────────────────────────────

const OnboardingWizard = ({ userName, userId, onComplete, onFinished }: Props) => {
  const [step, setStep] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLaunching, setIsLaunching] = useState(false);
  const [genError, setGenError] = useState("");
  const [launchError, setLaunchError] = useState("");
  const [loadingMsgIndex, setLoadingMsgIndex] = useState(0);
  // Live progress for the launch sequence so the long, multi-step launch
  // (image upload → voice clone → agent → save) shows real feedback instead
  // of a single static spinner. launchStepIndex points at the active step;
  // everything before it is done, everything after is pending.
  const [launchSteps, setLaunchSteps] = useState<{ key: string; label: string }[]>([]);
  const [launchStepIndex, setLaunchStepIndex] = useState(0);
  // True once the launch fully succeeds, so we can show a brief success screen
  // before handing off to the dashboard.
  const [launchSucceeded, setLaunchSucceeded] = useState(false);
  // Community slug (or id) returned by onComplete, used to link to the live
  // landing page at tribed.io/c/{slug} on the success screen.
  const [launchedSlug, setLaunchedSlug] = useState("");
  // Auto-advance fallback timer for the success screen.
  const redirectTimerRef = useRef<number | null>(null);
  // Inline confirmation for the destructive back action on the review step.
  const [showBackConfirm, setShowBackConfirm] = useState(false);
  // Which source input is visible on step 0. All three states are kept
  // regardless of the active tab, so switching tabs never loses input.
  const [sourceTab, setSourceTab] = useState<SourceTabKey>("link");

  // Heading focus target, moved on each step change for screen-reader users.
  const titleRef = useRef<HTMLHeadingElement>(null);

  // Temp ID for Firebase storage paths before community is created
  const tempId = useRef(`onb_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`);

  // Content sources
  const fileInputRef = useRef<HTMLInputElement>(null);
  const voiceSampleInputRef = useRef<HTMLInputElement>(null);
  const communityLogoInputRef = useRef<HTMLInputElement>(null);
  const authorPhotoInputRef = useRef<HTMLInputElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);
  const recordingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [websiteUrlError, setWebsiteUrlError] = useState("");
  const [pastedContent, setPastedContent] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [voiceSampleFile, setVoiceSampleFile] = useState<File | null>(null);
  const [voiceSamplePreviewUrl, setVoiceSamplePreviewUrl] = useState("");
  const [voiceCloneId, setVoiceCloneId] = useState("");
  const [agentId, setAgentId] = useState("");
  const [voiceCloneRequiresVerification, setVoiceCloneRequiresVerification] = useState(false);
  const [voiceCloneError, setVoiceCloneError] = useState("");
  // Picked starter voice, used when the creator does not import their own voice
  // sample. Empty means "let the launcher auto-pick from the community context".
  const [selectedDefaultVoiceId, setSelectedDefaultVoiceId] = useState("");

  // Images
  const [communityLogoFile, setCommunityLogoFile] = useState<File | null>(null);
  const [communityLogoPreview, setCommunityLogoPreview] = useState("");
  const [authorPhotoFile, setAuthorPhotoFile] = useState<File | null>(null);
  const [authorPhotoPreview, setAuthorPhotoPreview] = useState("");

  // Voice recording
  const [voiceInputMode, setVoiceInputMode] = useState<"upload" | "record">("upload");
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);

  // Step 0 progressive disclosure: branding (logo, photo, voice) stays hidden
  // until the creator opts in, so the first screen is just "add a source".
  const [showBranding, setShowBranding] = useState(false);

  // Chat editor (Step 2)
  type ChatMsg = { role: "user" | "assistant"; text: string };
  const [chatMessages, setChatMessages] = useState<ChatMsg[]>([]);
  const [chatInput, setChatInput] = useState("");
  const [isChatLoading, setIsChatLoading] = useState(false);
  const chatThreadRef = useRef<HTMLDivElement>(null);

  // Generated content
  const [fullContent, setFullContent] = useState<FullCommunityContent | null>(null);
  const [enabledHabits, setEnabledHabits] = useState<boolean[]>([]);
  const [enabledPlans, setEnabledPlans] = useState<boolean[]>([]);
  const [enabledQuotes, setEnabledQuotes] = useState<boolean[]>([]);

  // Section open state
  const [openAuthor, setOpenAuthor] = useState(true);
  const [openHabits, setOpenHabits] = useState(true);
  const [openPlan, setOpenPlan] = useState(true);
  const [openQuotes, setOpenQuotes] = useState(true);
  const [openQuestionnaire, setOpenQuestionnaire] = useState(true);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Community draft fields
  const [draftTitle, setDraftTitle] = useState("");
  const [draftDescription, setDraftDescription] = useState("");
  const [draftShortDescription, setDraftShortDescription] = useState("");
  const [draftCategoryName, setDraftCategoryName] = useState("");
  const [draftTags, setDraftTags] = useState("");
  const [draftWritingStyle, setDraftWritingStyle] = useState("");
  const [draftVisualStyle, setDraftVisualStyle] = useState("");
  const [draftAuthorDescription, setDraftAuthorDescription] = useState("");
  const [draftAiGuidelines, setDraftAiGuidelines] = useState("");
  const [draftPrimaryColor, setDraftPrimaryColor] = useState("");
  const [draftSecondaryColor, setDraftSecondaryColor] = useState("");

  // ─── Draft autosave (Task: never lose work on refresh) ────────────────────
  const { load: loadDraft, save: saveDraft, clear: clearDraft } =
    useOnboardingDraft(userId ?? "");
  // Stored draft found at mount, offered via a "Resume where you left off"
  // bar instead of silently overwriting the fresh state.
  const [resumeSnapshot, setResumeSnapshot] =
    useState<OnboardingDraftSnapshot | null>(null);
  // Autosave stays off until we know whether the coach wants the stored
  // draft, so the empty first render can't clobber it.
  const [autosaveReady, setAutosaveReady] = useState(false);

  useEffect(() => {
    const stored = loadDraft();
    if (stored && snapshotHasContent(stored)) {
      setResumeSnapshot(stored);
    } else {
      setAutosaveReady(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyResumeSnapshot = () => {
    const snap = resumeSnapshot;
    if (!snap) return;
    setSourceTab(snap.sourceTab);
    setWebsiteUrl(snap.websiteUrl);
    setPastedContent(snap.pastedContent);
    setSelectedDefaultVoiceId(snap.selectedDefaultVoiceId);
    setFullContent(snap.fullContent);
    setEnabledHabits(snap.enabledHabits);
    setEnabledPlans(snap.enabledPlans);
    setEnabledQuotes(snap.enabledQuotes);
    setDraftTitle(snap.draftTitle);
    setDraftDescription(snap.draftDescription);
    setDraftShortDescription(snap.draftShortDescription);
    setDraftCategoryName(snap.draftCategoryName);
    setDraftTags(snap.draftTags);
    setDraftWritingStyle(snap.draftWritingStyle);
    setDraftVisualStyle(snap.draftVisualStyle);
    setDraftAuthorDescription(snap.draftAuthorDescription);
    setDraftAiGuidelines(snap.draftAiGuidelines);
    setDraftPrimaryColor(snap.draftPrimaryColor);
    setDraftSecondaryColor(snap.draftSecondaryColor);
    // Never restore into the transient generating step. With a generated
    // draft we land on review; otherwise back on sources.
    setStep(snap.fullContent ? 2 : 0);
    setResumeSnapshot(null);
    setAutosaveReady(true);
  };

  const discardResumeSnapshot = () => {
    clearDraft();
    setResumeSnapshot(null);
    setAutosaveReady(true);
  };

  useEffect(() => {
    if (!autosaveReady || launchSucceeded) return;
    const snap: OnboardingDraftSnapshot = {
      step,
      sourceTab,
      websiteUrl,
      pastedContent,
      fullContent,
      enabledHabits,
      enabledPlans,
      enabledQuotes,
      selectedDefaultVoiceId,
      draftTitle,
      draftDescription,
      draftShortDescription,
      draftCategoryName,
      draftTags,
      draftWritingStyle,
      draftVisualStyle,
      draftAuthorDescription,
      draftAiGuidelines,
      draftPrimaryColor,
      draftSecondaryColor,
    };
    if (!snapshotHasContent(snap)) return;
    saveDraft(snap);
  }, [
    autosaveReady,
    launchSucceeded,
    step,
    sourceTab,
    websiteUrl,
    pastedContent,
    fullContent,
    enabledHabits,
    enabledPlans,
    enabledQuotes,
    selectedDefaultVoiceId,
    draftTitle,
    draftDescription,
    draftShortDescription,
    draftCategoryName,
    draftTags,
    draftWritingStyle,
    draftVisualStyle,
    draftAuthorDescription,
    draftAiGuidelines,
    draftPrimaryColor,
    draftSecondaryColor,
    saveDraft,
  ]);

  // Clear the success auto-advance timer if the wizard unmounts first.
  useEffect(() => {
    return () => {
      if (redirectTimerRef.current) window.clearTimeout(redirectTimerRef.current);
    };
  }, []);

  // Cycle loading messages while generating
  useEffect(() => {
    if (step !== 1) return;
    const interval = setInterval(() => {
      setLoadingMsgIndex((i) => (i + 1) % LOADING_MESSAGES.length);
    }, 2500);
    return () => clearInterval(interval);
  }, [step]);

  // Move focus on each transition so screen-reader and keyboard users land on
  // the new content. On the first step we focus the URL field directly so a
  // coach can paste and go without a click; later steps focus the heading.
  useEffect(() => {
    if (step === 0) {
      const el = document.getElementById("websiteUrl") as HTMLInputElement | null;
      if (el) {
        el.focus();
        return;
      }
    }
    titleRef.current?.focus();
  }, [step]);

  // Warn before the user closes or reloads the tab while there is in-progress
  // work that isn't persisted yet (generating, reviewing, or launching). Once
  // the launch has succeeded we stop guarding so the hand-off is clean.
  useEffect(() => {
    const hasUnsavedWork =
      !launchSucceeded && (isGenerating || isLaunching || step === 1 || step === 2);
    if (!hasUnsavedWork) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isGenerating, isLaunching, step, launchSucceeded]);

  useEffect(() => {
    if (!voiceSampleFile) {
      setVoiceSamplePreviewUrl("");
      return;
    }

    const previewUrl = URL.createObjectURL(voiceSampleFile);
    setVoiceSamplePreviewUrl(previewUrl);

    return () => {
      URL.revokeObjectURL(previewUrl);
    };
  }, [voiceSampleFile]);

  useEffect(() => {
    if (!communityLogoFile) { setCommunityLogoPreview(""); return; }
    const url = URL.createObjectURL(communityLogoFile);
    setCommunityLogoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [communityLogoFile]);

  useEffect(() => {
    if (!authorPhotoFile) { setAuthorPhotoPreview(""); return; }
    const url = URL.createObjectURL(authorPhotoFile);
    setAuthorPhotoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [authorPhotoFile]);

  // Cleanup recording timer on unmount
  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
    };
  }, []);

  // Auto-scroll chat thread to bottom on new messages
  useEffect(() => {
    if (chatThreadRef.current) {
      chatThreadRef.current.scrollTop = chatThreadRef.current.scrollHeight;
    }
  }, [chatMessages, isChatLoading]);

  const uploadImageToFirebase = async (file: File, path: string): Promise<string> => {
    return (
      await uploadMedia({
        body: file,
        kind: "image",
        fileName: file.name,
        firebasePath: path,
      })
    ).url;
  };

  const populateDraftState = (d: GeneratedDraft) => {
    setDraftTitle(d.title ?? "");
    setDraftDescription(d.description ?? "");
    setDraftShortDescription(d.shortDescription ?? "");
    setDraftCategoryName(d.categoryName ?? "");
    setDraftTags((d.tags ?? []).join(", "));
    setDraftWritingStyle(d.writingStyle ?? "");
    setDraftVisualStyle(d.visualStyle ?? "");
    setDraftAuthorDescription(d.authorDescription ?? "");
    setDraftAiGuidelines(d.aiGuidelines ?? "");
    setDraftPrimaryColor(d.primaryColor ?? "");
    setDraftSecondaryColor(d.secondaryColor ?? "");
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    const results: UploadedFile[] = [];
    for (const file of files) {
      if (file.type === "text/plain" || file.name.endsWith(".md")) {
        const text = await file.text();
        results.push({ name: file.name, mimeType: "text/plain", text: text.slice(0, 8000) });
      } else {
        // PDF, DOCX, etc. — keep the raw File for Gemini Files API upload
        results.push({ name: file.name, mimeType: file.type || "application/octet-stream", file });
      }
    }
    setUploadedFiles((prev) => [...prev, ...results]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleChatSend = async () => {
    const msg = chatInput.trim();
    if (!msg || isChatLoading) return;

    setChatInput("");
    setChatMessages((prev) => [...prev, { role: "user", text: msg }]);
    setIsChatLoading(true);

    try {
      const result = await editDraftWithChat(
        {
          title: draftTitle,
          description: draftDescription,
          shortDescription: draftShortDescription,
          categoryName: draftCategoryName,
          tags: draftTags.split(",").map((t) => t.trim()).filter(Boolean),
          writingStyle: draftWritingStyle,
          visualStyle: draftVisualStyle,
          authorDescription: draftAuthorDescription,
          aiGuidelines: draftAiGuidelines,
          primaryColor: draftPrimaryColor,
          secondaryColor: draftSecondaryColor,
          authorName: fullContent?.author?.fullName,
        },
        msg
      );

      const c = result.changes;
      if (c.title !== undefined) setDraftTitle(c.title);
      if (c.description !== undefined) setDraftDescription(c.description);
      if (c.shortDescription !== undefined) setDraftShortDescription(c.shortDescription);
      if (c.categoryName !== undefined) setDraftCategoryName(c.categoryName);
      if (c.tags !== undefined) setDraftTags(c.tags.join(", "));
      if (c.writingStyle !== undefined) setDraftWritingStyle(c.writingStyle);
      if (c.visualStyle !== undefined) setDraftVisualStyle(c.visualStyle);
      if (c.authorDescription !== undefined) setDraftAuthorDescription(c.authorDescription);
      if (c.aiGuidelines !== undefined) setDraftAiGuidelines(c.aiGuidelines);
      if (c.primaryColor !== undefined) setDraftPrimaryColor(c.primaryColor);
      if (c.secondaryColor !== undefined) setDraftSecondaryColor(c.secondaryColor);

      setChatMessages((prev) => [
        ...prev,
        { role: "assistant", text: result.summary || "Done!" },
      ]);
    } catch {
      setChatMessages((prev) => [
        ...prev,
        { role: "assistant", text: "Something went wrong. Please try again." },
      ]);
    } finally {
      setIsChatLoading(false);
    }
  };

  const handleAnalyze = async () => {
    setStep(1);
    setIsGenerating(true);
    setGenError("");
    setLaunchError("");
    setLoadingMsgIndex(0);

    try {
      const content = await generateFullCommunityContent({
        websiteUrl: normalizeUrl(websiteUrl) || undefined,
        pastedContent: pastedContent || undefined,
        uploadedFiles: uploadedFiles.length > 0 ? uploadedFiles : undefined,
      });

      setFullContent(content);
      setEnabledHabits(content.habits.map(() => true));
      setEnabledPlans(content.plans.map(() => true));
      setEnabledQuotes(content.quotes.map(() => true));
      populateDraftState(content.community);
      setStep(2);
    } catch (err: any) {
      console.error("Onboarding generation error:", err);
      setGenError(
        `We couldn't analyze your content: ${describeError(err)}`
      );
      setStep(0);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleVoiceSampleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if ((file.type || "").startsWith("video/")) {
      setVoiceCloneError("That looks like a video file. ElevenLabs voice cloning needs an audio-only sample, so please extract the reel audio first and upload MP3, WAV, M4A, OGG, FLAC, or audio WebM.");
      if (voiceSampleInputRef.current) voiceSampleInputRef.current.value = "";
      return;
    }

    if (file.size <= 0) {
      setVoiceCloneError("The selected audio file is empty. Please upload a valid voice sample.");
      if (voiceSampleInputRef.current) voiceSampleInputRef.current.value = "";
      return;
    }

    const isAudioFile =
      (file.type || "").startsWith("audio/") ||
      AUDIO_SAMPLE_EXTENSION_REGEX.test(file.name);

    if (!isAudioFile) {
      setVoiceCloneError("Please upload an audio file such as MP3, WAV, M4A, OGG, FLAC, or WebM.");
      if (voiceSampleInputRef.current) voiceSampleInputRef.current.value = "";
      return;
    }

    setVoiceSampleFile(file);
    setVoiceCloneId("");
    setAgentId("");
    setVoiceCloneRequiresVerification(false);
    setVoiceCloneError("");
    setLaunchError("");
    if (voiceSampleInputRef.current) voiceSampleInputRef.current.value = "";
  };

  const handleRemoveVoiceSample = () => {
    setVoiceSampleFile(null);
    setVoiceCloneId("");
    setAgentId("");
    setVoiceCloneRequiresVerification(false);
    setVoiceCloneError("");
    setLaunchError("");
    if (voiceSampleInputRef.current) voiceSampleInputRef.current.value = "";
  };

  const handleCommunityLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setCommunityLogoFile(file);
    if (communityLogoInputRef.current) communityLogoInputRef.current.value = "";
  };

  const handleAuthorPhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setAuthorPhotoFile(file);
    if (authorPhotoInputRef.current) authorPhotoInputRef.current.value = "";
  };

  const handleStartRecording = async () => {
    setVoiceCloneError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recordedChunksRef.current = [];
      setRecordingSeconds(0);

      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : "";

      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) recordedChunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);

        const blob = new Blob(recordedChunksRef.current, { type: mimeType || "audio/webm" });
        const ext = mimeType.includes("webm") ? "webm" : "wav";
        const file = new File([blob], `voice_recording.${ext}`, { type: mimeType || "audio/webm" });

        setVoiceSampleFile(file);
        setVoiceCloneId("");
        setAgentId("");
        setVoiceCloneRequiresVerification(false);
        setLaunchError("");
        setIsRecording(false);
      };

      mediaRecorderRef.current = recorder;
      recorder.start(100);
      setIsRecording(true);

      recordingTimerRef.current = setInterval(() => {
        setRecordingSeconds((s) => s + 1);
      }, 1000);
    } catch (err) {
      setVoiceCloneError("Could not access your microphone. Please allow microphone permission and try again.");
    }
  };

  const handleStopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
  };

  const getVoiceCloneName = () => {
    const authorName = fullContent?.author?.fullName?.trim();
    if (authorName) return `${authorName} Voice`;

    const communityName = draftTitle.trim();
    if (communityName) return `${communityName} Voice`;

    const fallbackName = userName.trim() || "Community";
    return `${fallbackName} Voice`;
  };

  const handleLaunch = async () => {
    setIsLaunching(true);
    setLaunchError("");
    setVoiceCloneError("");

    // Plan the steps we're about to run so the launching screen can show a
    // live checklist. Image and voice steps are conditional; agent and save
    // always run.
    const willUploadImages = !!communityLogoFile || !!authorPhotoFile;
    const willCloneVoice = !!voiceSampleFile && !voiceCloneId.trim();
    const planned = [
      ...(willUploadImages ? [{ key: "images", label: "Uploading your images" }] : []),
      ...(willCloneVoice ? [{ key: "voice", label: "Cloning your voice" }] : []),
      { key: "agent", label: "Creating your AI coach" },
      { key: "save", label: "Saving your community" },
    ];
    setLaunchSteps(planned);
    setLaunchStepIndex(0);
    const goToStep = (key: string) => {
      const idx = planned.findIndex((s) => s.key === key);
      if (idx >= 0) setLaunchStepIndex(idx);
    };

    // Upload images to Firebase (use tempId as the storage path prefix)
    let uploadedLogoUrl = "";
    let uploadedAuthorPhotoUrl = "";
    if (willUploadImages) goToStep("images");
    try {
      if (communityLogoFile) {
        uploadedLogoUrl = await uploadImageToFirebase(
          communityLogoFile,
          `onboarding/${tempId.current}/logo_${communityLogoFile.name}`
        );
      }
      if (authorPhotoFile) {
        uploadedAuthorPhotoUrl = await uploadImageToFirebase(
          authorPhotoFile,
          `onboarding/${tempId.current}/author_${authorPhotoFile.name}`
        );
      }
    } catch (err) {
      console.warn("Image upload failed during onboarding:", err);
      // Non-blocking — continue without images
    }

    const finalDraft: GeneratedDraft = {
      title: draftTitle,
      description: draftDescription,
      shortDescription: draftShortDescription,
      categoryName: draftCategoryName,
      tags: draftTags.split(",").map((t) => t.trim()).filter(Boolean),
      writingStyle: draftWritingStyle,
      visualStyle: draftVisualStyle,
      authorDescription: draftAuthorDescription,
      aiGuidelines: draftAiGuidelines,
      primaryColor: draftPrimaryColor,
      secondaryColor: draftSecondaryColor,
      logoURL: uploadedLogoUrl || undefined,
    };

    try {
      let clonedVoiceId = voiceCloneId.trim();
      if (voiceSampleFile && !clonedVoiceId) {
        goToStep("voice");
        try {
          const cloneResult = await createInstantVoiceClone({
            file: voiceSampleFile,
            name: getVoiceCloneName(),
            description: draftTitle.trim()
              ? `Voice clone created during onboarding for ${draftTitle.trim()}.`
              : "Voice clone created during onboarding.",
          });

          clonedVoiceId = cloneResult.voiceId;
          setVoiceCloneId(clonedVoiceId);
          setVoiceCloneRequiresVerification(cloneResult.requiresVerification);
        } catch (error) {
          console.warn("Voice clone creation skipped during onboarding:", error);
          setVoiceCloneError(
            "We couldn't create the voice clone during onboarding. The community will still launch, and you can add the voice later from the author form."
          );
        }
      }

      const draftTagList = draftTags.split(",").map((tag) => tag.trim()).filter(Boolean);

      // Voice precedence: a cloned voice wins; otherwise the voice the creator
      // explicitly picked; otherwise a default mapped from the community context
      // so the community still launches with a working voice agent instead of
      // skipping it. The creator can swap in their own voice later.
      const agentVoiceId =
        clonedVoiceId ||
        selectedDefaultVoiceId ||
        pickDefaultAgentVoice({
          categoryName: draftCategoryName.trim() || fullContent?.community?.categoryName,
          writingStyle: draftWritingStyle.trim() || fullContent?.community?.writingStyle,
          tags: draftTagList.length ? draftTagList : fullContent?.community?.tags,
        }).id;

      goToStep("agent");
      let createdAgentId = agentId.trim();
      if (agentVoiceId && !createdAgentId) {
        try {
          const agentResult = await createCommunityAgent({
            communityName: draftTitle.trim() || fullContent?.community?.title || `${userName} Community`,
            communityDescription: draftDescription.trim() || fullContent?.community?.description,
            categoryName: draftCategoryName.trim() || fullContent?.community?.categoryName,
            tags: draftTagList.length ? draftTagList : fullContent?.community?.tags,
            writingStyle: draftWritingStyle.trim() || fullContent?.community?.writingStyle,
            authorName: fullContent?.author?.fullName || userName,
            authorDescription: draftAuthorDescription.trim() || fullContent?.author?.description,
            communityAiGuidelines: draftAiGuidelines.trim() || fullContent?.community?.aiGuidelines,
            authorAiGuidelines: fullContent?.author?.aiGuidelines,
            voiceId: agentVoiceId,
            language: fullContent?.community?.lang,
          });

          createdAgentId = agentResult.agentId;
          setAgentId(createdAgentId);
        } catch (error) {
          console.warn("Agent creation skipped during onboarding:", error);
          setVoiceCloneError((prev) =>
            prev ||
            "The community launched without an ElevenLabs agent. You can add it later from the author form."
          );
        }
      }

      const finalContent: StarterContent | undefined = fullContent
        ? {
            author: {
              ...fullContent.author,
              voiceId: agentVoiceId,
              agentId: createdAgentId,
              profilePhotoURL: uploadedAuthorPhotoUrl || undefined,
            },
            habits: fullContent.habits.filter((_, i) => enabledHabits[i]),
            plans: fullContent.plans.filter((_, i) => enabledPlans[i]),
            quotes: fullContent.quotes.filter((_, i) => enabledQuotes[i]),
            questionnaire: fullContent.questionnaire,
          }
        : undefined;

      goToStep("save");
      const slugResult = await onComplete(finalDraft, finalContent);
      const slug = typeof slugResult === "string" ? slugResult.trim() : "";
      setLaunchStepIndex(planned.length);
      // The community is saved, so the local draft has done its job.
      clearDraft();
      // Show the success screen with the live page link. Auto-advance stays
      // as a fallback so nobody gets stuck: long enough to click through to
      // the page when there is one, short when there is nothing to show. If
      // no onFinished was provided, the parent owns the transition.
      if (onFinished) {
        setLaunchedSlug(slug);
        setLaunchSucceeded(true);
        redirectTimerRef.current = window.setTimeout(
          () => onFinished(),
          slug ? 10000 : 1800
        );
      }
    } catch (err: any) {
      console.error("Onboarding launch error:", err);
      setLaunchError(
        `We couldn't finish launching your community: ${describeError(err)}`
      );
    } finally {
      setIsLaunching(false);
    }
  };

  const handleGoToDashboard = () => {
    if (redirectTimerRef.current) window.clearTimeout(redirectTimerRef.current);
    onFinished?.();
  };

  // Destructive reset behind the inline back confirmation on the review step.
  const confirmBackToSources = () => {
    setShowBackConfirm(false);
    setStep(0);
    setFullContent(null);
    setGenError("");
    setLaunchError("");
  };

  // Roving focus for the source tabs: left/right arrows move both selection
  // and focus, per the WAI-ARIA tabs pattern.
  const handleSourceTabKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    e.preventDefault();
    const idx = SOURCE_TABS.findIndex((t) => t.key === sourceTab);
    const next =
      e.key === "ArrowRight"
        ? (idx + 1) % SOURCE_TABS.length
        : (idx + SOURCE_TABS.length - 1) % SOURCE_TABS.length;
    const key = SOURCE_TABS[next].key;
    setSourceTab(key);
    document.getElementById(`source-tab-${key}`)?.focus();
  };

  const autoResize = (el: HTMLTextAreaElement | null) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = el.scrollHeight + "px";
  };

  // Accept URLs with or without a protocol ("yoursite.com" → "https://yoursite.com").
  const normalizeUrl = (raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed) return "";
    return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  };

  const isLikelyUrl = (raw: string) => {
    if (!raw.trim()) return true; // empty is fine, the field is optional
    try {
      const url = new URL(normalizeUrl(raw));
      return !!url.hostname && url.hostname.includes(".");
    } catch {
      return false;
    }
  };

  const handleWebsiteUrlBlur = () => {
    if (websiteUrl.trim() && !isLikelyUrl(websiteUrl)) {
      setWebsiteUrlError("That doesn't look like a valid URL. Example: yoursite.com");
    } else {
      setWebsiteUrlError("");
    }
  };

  // A website URL only counts as a usable source when it looks valid.
  const validWebsiteUrl =
    websiteUrl.trim() && isLikelyUrl(websiteUrl) ? normalizeUrl(websiteUrl) : "";

  const hasSource =
    !!validWebsiteUrl || !!pastedContent.trim() || uploadedFiles.length > 0;

  // How many optional branding inputs the creator has filled, shown on the
  // collapsed disclosure toggle so hidden work stays visible.
  const brandingCount =
    (communityLogoFile ? 1 : 0) +
    (authorPhotoFile ? 1 : 0) +
    (voiceSampleFile || selectedDefaultVoiceId ? 1 : 0);

  const canProceed = () => {
    if (step === 0) return hasSource;
    if (step === 2) return !!draftTitle.trim() && !!fullContent;
    return false;
  };

  const renderStars = (stars?: number) => "★".repeat(Math.max(1, Math.min(5, stars || 5)));

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <Wizard>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <LogoutBtn
          type="button"
          disabled={isLaunching || launchSucceeded}
          style={
            isLaunching || launchSucceeded
              ? { opacity: 0.4, pointerEvents: "none" }
              : undefined
          }
          onClick={() => firebase.auth().signOut()}
        >
          Log out
        </LogoutBtn>
      </div>

      <StepBar
        role="progressbar"
        aria-valuemin={1}
        aria-valuemax={STEP_META.length}
        aria-valuenow={step + 1}
        aria-label={`Onboarding step ${step + 1} of ${STEP_META.length}`}
      >
        {STEP_META.map((_, i) => (
          <StepSegment key={i} active={i <= step} />
        ))}
      </StepBar>

      <Header>
        {step === 2 && !isLaunching && !launchSucceeded && (
          <>
            <SectionNav>
              <BackBtn
                type="button"
                aria-label="Back to content sources"
                aria-expanded={showBackConfirm}
                onClick={() => setShowBackConfirm((v) => !v)}
              >
                <ArrowBack fontSize="small" />
              </BackBtn>
            </SectionNav>
            {showBackConfirm && (
              <NoticeBar
                role="alertdialog"
                aria-label="Confirm going back"
                style={{ marginBottom: 10 }}
                onKeyDown={(e) => {
                  if (e.key === "Escape") setShowBackConfirm(false);
                }}
              >
                Going back clears the generated community and any edits you made.
                <NoticeActions>
                  <SmallBtn
                    type="button"
                    autoFocus
                    onClick={() => setShowBackConfirm(false)}
                  >
                    Cancel
                  </SmallBtn>
                  <SmallBtn tone="danger" type="button" onClick={confirmBackToSources}>
                    Go back
                  </SmallBtn>
                </NoticeActions>
              </NoticeBar>
            )}
          </>
        )}
        <Title ref={titleRef} tabIndex={-1}>
          {launchSucceeded
            ? "You're all set!"
            : isLaunching
            ? "Launching your community…"
            : step === 0
            ? `Welcome, ${userName}!`
            : STEP_META[step].title}
        </Title>
        <Subtitle>
          {launchSucceeded
            ? launchedSlug
              ? "Your community is live. Take a look at your public page."
              : "Your community is live. Taking you to your dashboard."
            : isLaunching
            ? "Sit tight while we set everything up. Please keep this tab open."
            : STEP_META[step].subtitle}
        </Subtitle>
      </Header>

      {/* ── Step 0: Content Sources ── */}
      {step === 0 && (
        <>
          {resumeSnapshot && (
            <NoticeBar role="region" aria-label="Resume saved draft">
              <div>
                <strong>Resume where you left off?</strong> We saved your
                progress{resumeSnapshot.fullContent
                  ? ", including the community the agent built."
                  : "."}
              </div>
              <NoticeActions>
                <SmallBtn type="button" onClick={discardResumeSnapshot}>
                  Start fresh
                </SmallBtn>
                <SmallBtn tone="primary" type="button" onClick={applyResumeSnapshot}>
                  Resume
                </SmallBtn>
              </NoticeActions>
            </NoticeBar>
          )}

          <div>
            <SourceTabBar role="tablist" aria-label="Content source">
              {SOURCE_TABS.map((t) => {
                const filled =
                  t.key === "link"
                    ? !!validWebsiteUrl
                    : t.key === "paste"
                    ? !!pastedContent.trim()
                    : uploadedFiles.length > 0;
                const active = sourceTab === t.key;
                return (
                  <SourceTab
                    key={t.key}
                    id={`source-tab-${t.key}`}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    aria-controls={`source-panel-${t.key}`}
                    tabIndex={active ? 0 : -1}
                    active={active}
                    onClick={() => setSourceTab(t.key)}
                    onKeyDown={handleSourceTabKeyDown}
                  >
                    {t.label}
                    {filled && <SourceTabDot aria-hidden="true" />}
                  </SourceTab>
                );
              })}
            </SourceTabBar>
          </div>

          <div
            role="tabpanel"
            id={`source-panel-${sourceTab}`}
            aria-labelledby={`source-tab-${sourceTab}`}
          >
            {sourceTab === "link" && (
              <InputWrapper>
                <Input
                  onChange={(e: any) => {
                    setWebsiteUrl(e.target.value);
                    if (websiteUrlError) setWebsiteUrlError("");
                    if (genError) setGenError("");
                  }}
                  onBlur={handleWebsiteUrlBlur}
                  onKeyDown={(e: any) => {
                    if (e.key === "Enter" && hasSource) {
                      e.preventDefault();
                      handleAnalyze();
                    }
                  }}
                  value={websiteUrl}
                  label="Your website URL"
                  placeholder="yoursite.com"
                  id="websiteUrl"
                  type="url"
                  error={websiteUrlError}
                  endAdornment={
                    validWebsiteUrl ? (
                      <CheckCircleIcon style={{ fontSize: 18, color: T.green }} />
                    ) : undefined
                  }
                />
              </InputWrapper>
            )}

            {sourceTab === "paste" && (
              <div>
                <FieldLabel htmlFor="pastedContent">Paste your content</FieldLabel>
                <TextArea
                  id="pastedContent"
                  placeholder="Paste your Instagram bio, LinkedIn about, YouTube description, tweets, course outlines, anything that represents your brand…"
                  value={pastedContent}
                  onChange={(e) => {
                    setPastedContent(e.target.value);
                    if (genError) setGenError("");
                  }}
                />
              </div>
            )}

            {sourceTab === "upload" && (
              <div>
                <FieldLabel>Upload a file</FieldLabel>
                <UploadZone>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".txt,.md,.pdf,.doc,.docx"
                    style={{ display: "none" }}
                    onChange={handleFileUpload}
                  />
                  <CloudUploadIcon style={{ fontSize: 28 }} />
                  <span>Click to upload PDF, TXT, DOCX…</span>
                </UploadZone>
                {uploadedFiles.length > 0 && (
                  <FileList>
                    {uploadedFiles.map((f, i) => (
                      <FileItem key={i}>
                        <CheckCircleIcon style={{ fontSize: 16, color: T.green }} />
                        {f.name}
                      </FileItem>
                    ))}
                  </FileList>
                )}
              </div>
            )}
          </div>

          <div>
            <AdvancedToggle type="button" onClick={() => setShowBranding((v) => !v)}>
              {showBranding ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              {showBranding ? "Hide" : "Add"} logo, photo &amp; voice (optional)
              {!showBranding && brandingCount > 0 && (
                <SectionCount style={{ marginLeft: 4 }}>{brandingCount} added</SectionCount>
              )}
            </AdvancedToggle>
          </div>

          {showBranding && (
            <>
          <div>
            <FieldLabel>Community logo &amp; author photo (optional)</FieldLabel>
            <PhotoRow>
              <div style={{ flex: 1 }}>
                <input
                  ref={communityLogoInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  id="community-logo-input"
                  onChange={handleCommunityLogoChange}
                />
                <PhotoUploadBox htmlFor="community-logo-input">
                  {communityLogoPreview ? (
                    <LogoPreview src={communityLogoPreview} alt="Logo" />
                  ) : (
                    <span style={{ fontSize: 22, lineHeight: 1 }}>🏷️</span>
                  )}
                  <span>{communityLogoFile ? communityLogoFile.name.slice(0, 20) : "Community logo"}</span>
                </PhotoUploadBox>
              </div>
              <div style={{ flex: 1 }}>
                <input
                  ref={authorPhotoInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  id="author-photo-input"
                  onChange={handleAuthorPhotoChange}
                />
                <PhotoUploadBox htmlFor="author-photo-input">
                  {authorPhotoPreview ? (
                    <PhotoPreview src={authorPhotoPreview} alt="Author" />
                  ) : (
                    <span style={{ fontSize: 22, lineHeight: 1 }}>👤</span>
                  )}
                  <span>{authorPhotoFile ? authorPhotoFile.name.slice(0, 20) : "Author photo"}</span>
                </PhotoUploadBox>
              </div>
            </PhotoRow>
          </div>

          <div>
            <FieldLabel>Voice sample (optional)</FieldLabel>
            <VoiceTabBar>
              <VoiceTab
                type="button"
                active={voiceInputMode === "upload"}
                onClick={() => setVoiceInputMode("upload")}
              >
                Upload file
              </VoiceTab>
              <VoiceTab
                type="button"
                active={voiceInputMode === "record"}
                onClick={() => setVoiceInputMode("record")}
              >
                Record now
              </VoiceTab>
            </VoiceTabBar>
            <UploadZone style={{ display: voiceInputMode === "upload" ? undefined : "none" }}>
              <input
                ref={voiceSampleInputRef}
                type="file"
                accept="audio/*,.mp3,.wav,.m4a,.aac,.ogg,.flac,.webm"
                style={{ display: "none" }}
                onChange={handleVoiceSampleUpload}
              />
              <CloudUploadIcon style={{ fontSize: 28 }} />
              <span>Click to upload a short voice sample</span>
            </UploadZone>
            {voiceInputMode === "record" && (
              <RecordBtn
                type="button"
                recording={isRecording}
                onClick={isRecording ? handleStopRecording : handleStartRecording}
              >
                {isRecording ? (
                  <>
                    <RecordDot />
                    Recording… {recordingSeconds}s — tap to stop
                  </>
                ) : voiceSampleFile?.name === "voice_recording.webm" || voiceSampleFile?.name === "voice_recording.wav" ? (
                  <>
                    <span style={{ fontSize: 16 }}>🎙️</span>
                    Re-record voice sample
                  </>
                ) : (
                  <>
                    <span style={{ fontSize: 16 }}>🎙️</span>
                    Tap to start recording
                  </>
                )}
              </RecordBtn>
            )}
            <HelperText>
              {voiceInputMode === "upload"
                ? "Upload 5-30 seconds of clear speech. We'll try to create an ElevenLabs voice clone and community agent when you launch. If either step fails, the community will still be created and you can configure them later from the author form."
                : "Record 5-30 seconds of clear speech. Your browser will ask for microphone permission. Tap Stop when done."}
            </HelperText>
            {voiceSampleFile && (
              <>
                <FileList>
                  <FileItem>
                    <CheckCircleIcon style={{ fontSize: 16, color: T.green }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, color: T.textMid, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {voiceSampleFile.name.startsWith("voice_recording")
                          ? `Voice recording${recordingSeconds > 0 ? ` — ${recordingSeconds}s` : ""}`
                          : voiceSampleFile.name}
                      </div>
                      <div style={{ fontSize: 11, color: T.textVeryLow, marginTop: 2 }}>
                        {voiceCloneId
                          ? `Voice clone ready: ${voiceCloneId}`
                          : "Voice clone and agent will be created on launch"}
                      </div>
                    </div>
                    <IconButton
                      size="small"
                      onClick={handleRemoveVoiceSample}
                      style={{ color: T.textVeryLow, padding: 4, flexShrink: 0 }}
                    >
                      <CloseIcon style={{ fontSize: 16 }} />
                    </IconButton>
                  </FileItem>
                </FileList>
                {voiceSamplePreviewUrl && (
                  <audio controls src={voiceSamplePreviewUrl} style={{ width: "100%", marginTop: 10 }} />
                )}
              </>
            )}
            {!voiceSampleFile && (
              <div style={{ marginTop: 16 }}>
                <FieldLabel>Or pick a starter voice</FieldLabel>
                <VoiceOptionGrid>
                  {DEFAULT_AGENT_VOICES.map((voice) => {
                    const selected = selectedDefaultVoiceId === voice.id;
                    return (
                      <VoiceOption
                        key={voice.id}
                        type="button"
                        selected={selected}
                        onClick={() =>
                          setSelectedDefaultVoiceId(selected ? "" : voice.id)
                        }
                      >
                        <VoiceOptionName selected={selected}>{voice.label}</VoiceOptionName>
                        <VoiceOptionMeta>
                          {voice.persona} · {voice.gender}
                        </VoiceOptionMeta>
                      </VoiceOption>
                    );
                  })}
                </VoiceOptionGrid>
                <HelperText>
                  No voice sample? Pick a voice for the agent. If you skip this, we choose one that fits the community automatically.
                </HelperText>
              </div>
            )}
            {voiceCloneError && (
              <ErrorMsg style={{ textAlign: "left", marginTop: 8 }}>{voiceCloneError}</ErrorMsg>
            )}
          </div>
            </>
          )}

          {genError && <ErrorBanner>{genError}</ErrorBanner>}
        </>
      )}

      {/* ── Step 1: Generating ── */}
      {step === 1 && (
        <GeneratingState>
          <CircularProgress size={40} style={{ color: T.textMid }} aria-label="Loading" />
          <div role="status" aria-live="polite">
            <div style={{ fontWeight: 600, fontSize: 15, color: T.text, marginBottom: 8 }}>
              Building your community…
            </div>
            <GeneratingMsg>{LOADING_MESSAGES[loadingMsgIndex]}</GeneratingMsg>
          </div>
        </GeneratingState>
      )}

      {/* ── Step 2: Launching ── */}
      {step === 2 && isLaunching && !launchSucceeded && (
        <GeneratingState>
          <CircularProgress size={40} style={{ color: T.textMid }} aria-label="Loading" />
          <div role="status" aria-live="polite">
            <div style={{ fontWeight: 600, fontSize: 15, color: T.text, marginBottom: 8 }}>
              Almost there…
            </div>
            <GeneratingMsg>This usually takes 10-20 seconds.</GeneratingMsg>
          </div>
          <LaunchingSteps>
            {launchSteps.map((s, i) => {
              const status =
                i < launchStepIndex ? "done" : i === launchStepIndex ? "active" : "pending";
              return (
                <LaunchStepRow key={s.key} status={status}>
                  <LaunchStepIcon>
                    {status === "done" ? (
                      <CheckCircleIcon style={{ fontSize: 20, color: T.green }} />
                    ) : status === "active" ? (
                      <CircularProgress size={16} style={{ color: T.textMid }} aria-label="In progress" />
                    ) : (
                      <PendingDot />
                    )}
                  </LaunchStepIcon>
                  {s.label}
                </LaunchStepRow>
              );
            })}
          </LaunchingSteps>
        </GeneratingState>
      )}

      {/* ── Step 2: Success ── */}
      {step === 2 && launchSucceeded && (
        <GeneratingState role="status" aria-live="polite">
          <SuccessCheck>
            <CheckCircleIcon style={{ fontSize: 56, color: T.green }} />
          </SuccessCheck>
          <div>
            <div style={{ fontWeight: 600, fontSize: 16, color: T.text, marginBottom: 8 }}>
              Your community is live!
            </div>
            <GeneratingMsg>
              {launchedSlug
                ? `tribed.io/c/${launchedSlug}`
                : "Taking you to your dashboard…"}
            </GeneratingMsg>
          </div>
          {launchedSlug && (
            <SuccessActions>
              <SeePageLink
                href={`https://tribed.io/c/${launchedSlug}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                See your page ↗
              </SeePageLink>
              <DashboardBtn type="button" onClick={handleGoToDashboard}>
                Go to dashboard
              </DashboardBtn>
            </SuccessActions>
          )}
        </GeneratingState>
      )}

      {/* ── Step 2: Review ── */}
      {step === 2 && fullContent && !isLaunching && !launchSucceeded && (
        <ReviewContainer>

          {/* One-click launch with the agent's defaults */}
          <QuickLaunchBar>
            <div style={{ flex: 1, minWidth: 180 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>
                Happy with what the agent built?
              </div>
              <div style={{ fontSize: 12, color: T.textLow, marginTop: 2 }}>
                Launch now with these defaults. You can edit everything later
                from your dashboard.
              </div>
            </div>
            <Button
              variant="contained"
              color="primary"
              disabled={!canProceed()}
              onClick={handleLaunch}
              style={{ borderRadius: 10, fontWeight: 600, whiteSpace: "nowrap" }}
            >
              Launch now
            </Button>
          </QuickLaunchBar>

          {/* Community Profile */}
          <ReviewSection>
            {communityLogoPreview && (
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <LogoPreview src={communityLogoPreview} alt="Community logo" />
                <div style={{ fontSize: 11, color: T.textVeryLow }}>Community logo</div>
              </div>
            )}
            <CommunityNameInput
              value={draftTitle}
              onChange={(e) => setDraftTitle(e.target.value)}
              placeholder="Community name…"
            />

            <ChipGroup>
              {COMMUNITY_CATEGORIES.map((c) => (
                <Chip
                  key={c}
                  type="button"
                  selected={draftCategoryName === c}
                  onClick={() => setDraftCategoryName(c)}
                >
                  {c}
                </Chip>
              ))}
            </ChipGroup>

            <FieldGroup>
              <InlineLabel>Short description</InlineLabel>
              <InlineTextArea
                value={draftShortDescription}
                rows={2}
                onChange={(e) => {
                  setDraftShortDescription(e.target.value);
                  autoResize(e.target);
                }}
                ref={(el) => autoResize(el)}
                placeholder="One hook sentence…"
              />
            </FieldGroup>

            <FieldGroup>
              <InlineLabel>Full description</InlineLabel>
              <InlineTextArea
                value={draftDescription}
                rows={4}
                onChange={(e) => {
                  setDraftDescription(e.target.value);
                  autoResize(e.target);
                }}
                ref={(el) => autoResize(el)}
                placeholder="Full community description…"
              />
            </FieldGroup>

            <FieldGroup>
              <InlineLabel>Brand colors</InlineLabel>
              <ColorRow>
                <ColorField>
                  <ColorSwatch
                    type="color"
                    value={draftPrimaryColor || "#ffffff"}
                    onChange={(e) => setDraftPrimaryColor(e.target.value)}
                    title="Primary color"
                  />
                  <ColorHexInput
                    value={draftPrimaryColor}
                    onChange={(e) => setDraftPrimaryColor(e.target.value)}
                    placeholder="#hex"
                    maxLength={7}
                  />
                </ColorField>
                <ColorField>
                  <ColorSwatch
                    type="color"
                    value={draftSecondaryColor || "#ffffff"}
                    onChange={(e) => setDraftSecondaryColor(e.target.value)}
                    title="Secondary color"
                  />
                  <ColorHexInput
                    value={draftSecondaryColor}
                    onChange={(e) => setDraftSecondaryColor(e.target.value)}
                    placeholder="#hex"
                    maxLength={7}
                  />
                </ColorField>
              </ColorRow>
            </FieldGroup>

            <AdvancedToggle
              type="button"
              onClick={() => setShowAdvanced((v) => !v)}
            >
              {showAdvanced ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              {showAdvanced ? "Hide" : "Show"} advanced settings
            </AdvancedToggle>

            {showAdvanced && (
              <>
                <FieldGroup>
                  <InlineLabel>Tags</InlineLabel>
                  <InlineInput
                    value={draftTags}
                    onChange={(e) => setDraftTags(e.target.value)}
                    placeholder="tag1, tag2, tag3"
                  />
                </FieldGroup>
                <FieldGroup>
                  <InlineLabel>Writing style</InlineLabel>
                  <InlineTextArea
                    value={draftWritingStyle}
                    rows={3}
                    onChange={(e) => {
                      setDraftWritingStyle(e.target.value);
                      autoResize(e.target);
                    }}
                    ref={(el) => autoResize(el)}
                  />
                </FieldGroup>
                <FieldGroup>
                  <InlineLabel>Visual style</InlineLabel>
                  <InlineTextArea
                    value={draftVisualStyle}
                    rows={3}
                    onChange={(e) => {
                      setDraftVisualStyle(e.target.value);
                      autoResize(e.target);
                    }}
                    ref={(el) => autoResize(el)}
                  />
                </FieldGroup>
                <FieldGroup>
                  <InlineLabel>Author bio</InlineLabel>
                  <InlineTextArea
                    value={draftAuthorDescription}
                    rows={3}
                    onChange={(e) => {
                      setDraftAuthorDescription(e.target.value);
                      autoResize(e.target);
                    }}
                    ref={(el) => autoResize(el)}
                  />
                </FieldGroup>
                <FieldGroup>
                  <InlineLabel>AI content guidelines</InlineLabel>
                  <InlineTextArea
                    value={draftAiGuidelines}
                    rows={4}
                    onChange={(e) => {
                      setDraftAiGuidelines(e.target.value);
                      autoResize(e.target);
                    }}
                    ref={(el) => autoResize(el)}
                  />
                </FieldGroup>
              </>
            )}
          </ReviewSection>

          <Divider />

          {/* Author */}
          <ReviewSection>
            <SectionHeader type="button" onClick={() => setOpenAuthor((v) => !v)}>
              <SectionTitle>Author</SectionTitle>
              {openAuthor
                ? <ExpandLessIcon fontSize="small" style={{ color: T.textLow }} />
                : <ExpandMoreIcon fontSize="small" style={{ color: T.textLow }} />}
            </SectionHeader>
            {openAuthor && (
              <div style={{ padding: "4px 0 8px" }}>
                {authorPhotoPreview && (
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                    <PhotoPreview src={authorPhotoPreview} alt="Author photo" />
                    <div style={{ fontSize: 11, color: T.textVeryLow }}>Author photo</div>
                  </div>
                )}
                <div style={{ fontSize: 15, fontWeight: 600, color: T.text, marginBottom: 2 }}>{fullContent.author.fullName}</div>
                <div style={{ fontSize: 12, color: T.textMid, marginBottom: 8 }}>{fullContent.author.shortDescription}</div>
                <div style={{ fontSize: 12, color: T.textLow, marginBottom: 10 }}>{fullContent.author.description}</div>
                {fullContent.author.tags?.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
                    {fullContent.author.tags.map((tag, i) => (
                      <span key={i} style={{ fontSize: 11, padding: "2px 8px", borderRadius: 12, background: "var(--color-accent-subtle)", color: T.textLow }}>{tag}</span>
                    ))}
                  </div>
                )}
                {fullContent.author.aiGuidelines && (
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 600, color: T.textVeryLow, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>AI Guidelines</div>
                    <div style={{ fontSize: 11, color: T.textLow, padding: "6px 10px", background: "var(--color-surface-subtle)", borderRadius: 6 }}>{fullContent.author.aiGuidelines}</div>
                  </div>
                )}
                <div
                  style={{
                    marginTop: 12,
                    padding: "10px 12px",
                    borderRadius: 8,
                    border: `1px solid ${voiceCloneId ? T.greenBorder : T.border}`,
                    background: voiceCloneId ? T.greenBg : T.surface,
                  }}
                >
                  <div style={{ fontSize: 10, fontWeight: 600, color: T.textVeryLow, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>
                    Voice Clone
                  </div>
                  <div style={{ fontSize: 12, color: T.textMid }}>
                    {voiceSampleFile
                      ? voiceCloneId
                        ? `Ready with voice ID ${voiceCloneId}`
                        : "A voice clone will be created from your uploaded sample when you launch."
                      : "No voice sample uploaded during onboarding."}
                  </div>
                  {voiceSampleFile && (
                    <div style={{ fontSize: 11, color: T.textVeryLow, marginTop: 4 }}>
                      Sample: {voiceSampleFile.name}
                    </div>
                  )}
                  {voiceCloneRequiresVerification && (
                    <div style={{ fontSize: 11, color: T.textVeryLow, marginTop: 6 }}>
                      ElevenLabs marked this clone for verification before use.
                    </div>
                  )}
                  <div style={{ fontSize: 12, color: T.textMid, marginTop: 8 }}>
                    {voiceSampleFile
                      ? agentId
                        ? `Agent ready with ID ${agentId}`
                        : "A community agent will be created right after the voice clone."
                      : "No community agent will be created until a voice sample is uploaded."}
                  </div>
                </div>
              </div>
            )}
          </ReviewSection>

          <Divider />

          {/* Habits */}
          <ReviewSection>
            <SectionHeader type="button" onClick={() => setOpenHabits((v) => !v)}>
              <SectionTitle>
                Habits
                <SectionCount>
                  {enabledHabits.filter(Boolean).length} / {fullContent.habits.length}
                </SectionCount>
              </SectionTitle>
              {openHabits
                ? <ExpandLessIcon fontSize="small" style={{ color: T.textLow }} />
                : <ExpandMoreIcon fontSize="small" style={{ color: T.textLow }} />}
            </SectionHeader>

            {openHabits && fullContent.habits.map((h, i) => (
              <HabitCard key={i} enabled={enabledHabits[i]}>
                <HabitInfo>
                  <HabitName>{h.habitName}</HabitName>
                  <HabitDesc>{h.habitDescription}</HabitDesc>
                  <FreqBadge>{h.targetFrequency}</FreqBadge>
                  {h.actions?.length > 0 && (
                    <div style={{ marginTop: 10 }}>
                      {h.actions.map((a, ai) => (
                        <div key={ai} style={{ marginBottom: 8 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: T.textMid, marginBottom: 4 }}>
                            {a.name}
                            <span style={{ fontWeight: 400, color: T.textLow, marginLeft: 8 }}>
                              {a.actionType}{a.duration ? ` · ${a.duration}min` : ""}
                            </span>
                          </div>
                          {a.subactions?.map((s, si) => (
                            <div key={si} style={{
                              padding: "6px 10px",
                              marginBottom: 4,
                              marginLeft: 10,
                              background: "var(--color-surface-subtle)",
                              borderRadius: 6,
                              borderLeft: "3px solid var(--color-border)",
                            }}>
                              <div style={{ fontSize: 11, fontWeight: 600, color: T.textMid }}>
                                {s.name}
                                <span style={{ fontWeight: 400, color: T.textLow, marginLeft: 8 }}>
                                  {s.duration ? `${s.duration}min` : ""}
                                  {s.intensityLevel ? ` · ${s.intensityLevel}` : ""}
                                </span>
                              </div>
                              <div style={{ fontSize: 11, color: T.textLow, marginTop: 2 }}>{s.description}</div>
                              {s.aiPrompt && (
                                <div style={{ fontSize: 10, color: T.textVeryLow, marginTop: 3, padding: "3px 5px", background: "var(--color-surface-subtle)", borderRadius: 4 }}>
                                  AI coached
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </HabitInfo>
                <Switch
                  checked={enabledHabits[i]}
                  onChange={() =>
                    setEnabledHabits((prev) =>
                      prev.map((v, idx) => (idx === i ? !v : v))
                    )
                  }
                  color="primary"
                  size="small"
                />
              </HabitCard>
            ))}
            {openHabits && fullContent.habits.length === 0 && (
              <EmptyState>
                No habits came back from your content. Add more detail to your source and regenerate, or create habits later from your dashboard.
              </EmptyState>
            )}
          </ReviewSection>

          <Divider />

          {/* Learning Plans */}
          <ReviewSection>
            <SectionHeader type="button" onClick={() => setOpenPlan((v) => !v)}>
              <SectionTitle>
                Learning Plans
                <SectionCount>{enabledPlans.filter(Boolean).length} / {fullContent.plans.length}</SectionCount>
              </SectionTitle>
              {openPlan
                ? <ExpandLessIcon fontSize="small" style={{ color: T.textLow }} />
                : <ExpandMoreIcon fontSize="small" style={{ color: T.textLow }} />}
            </SectionHeader>

            {openPlan && fullContent.plans.map((plan, pi) => (
              <div key={pi} style={{ marginBottom: 12, opacity: enabledPlans[pi] ? 1 : 0.4 }}>
                <PlanHeader style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                  <div>
                    <PlanTitle>{plan.title}</PlanTitle>
                    <PlanDesc>{plan.shortDescription}</PlanDesc>
                    <div style={{ fontSize: 11, color: T.textLow, marginTop: 2 }}>{plan.level} · {plan.dayPlans.length} days</div>
                  </div>
                  <Switch
                    checked={enabledPlans[pi]}
                    onChange={() => setEnabledPlans((prev) => prev.map((v, idx) => idx === pi ? !v : v))}
                    size="small"
                    color="primary"
                  />
                </PlanHeader>
                {enabledPlans[pi] && (
                  <PlanDayList>
                    {plan.dayPlans.map((day, i) => (
                      <PlanDayRow key={i}>
                        <DayNumber>Day {i + 1}</DayNumber>
                        <DayContent>
                          <DayTitle>{day.title}</DayTitle>
                          <DaySteps>{day.steps.map((s) => s.title).join(" · ")}</DaySteps>
                        </DayContent>
                      </PlanDayRow>
                    ))}
                  </PlanDayList>
                )}
              </div>
            ))}
            {openPlan && fullContent.plans.length === 0 && (
              <EmptyState>
                No learning plan was generated yet. You can build one anytime from your dashboard.
              </EmptyState>
            )}
          </ReviewSection>

          <Divider />

          {/* Quotes */}
          <ReviewSection>
            <SectionHeader type="button" onClick={() => setOpenQuotes((v) => !v)}>
              <SectionTitle>
                Quotes
                <SectionCount>
                  {enabledQuotes.filter(Boolean).length} / {fullContent.quotes.length}
                </SectionCount>
              </SectionTitle>
              {openQuotes
                ? <ExpandLessIcon fontSize="small" style={{ color: T.textLow }} />
                : <ExpandMoreIcon fontSize="small" style={{ color: T.textLow }} />}
            </SectionHeader>

            {openQuotes && fullContent.quotes.map((q, i) =>
              enabledQuotes[i] ? (
                <QuoteCard key={i}>
                  <QuoteContent>
                    <QuoteText>"{q.quote}"</QuoteText>
                    <QuoteAuthor>
                      — {q.authorName}
                    </QuoteAuthor>
                  </QuoteContent>
                  <IconButton
                    size="small"
                    onClick={() =>
                      setEnabledQuotes((prev) =>
                        prev.map((v, idx) => (idx === i ? false : v))
                      )
                    }
                    style={{ color: T.textVeryLow, padding: 4, flexShrink: 0 }}
                  >
                    <CloseIcon style={{ fontSize: 16 }} />
                  </IconButton>
                </QuoteCard>
              ) : null
            )}
            {openQuotes && enabledQuotes.filter(Boolean).length === 0 && (
              <EmptyState>
                {fullContent.quotes.length === 0
                  ? "No quotes were generated. You can add your own later from your dashboard."
                  : "All quotes removed. Add them back or create new ones later from your dashboard."}
              </EmptyState>
            )}
          </ReviewSection>

          <Divider />

          {/* Questionnaire */}
          <ReviewSection>
            <SectionHeader type="button" onClick={() => setOpenQuestionnaire((v) => !v)}>
              <SectionTitle>
                Questionnaire
                <SectionCount>{fullContent.questionnaire.questions.length} questions</SectionCount>
              </SectionTitle>
              {openQuestionnaire
                ? <ExpandLessIcon fontSize="small" style={{ color: T.textLow }} />
                : <ExpandMoreIcon fontSize="small" style={{ color: T.textLow }} />}
            </SectionHeader>
            {openQuestionnaire && (
              <div style={{ paddingTop: 4 }}>
                {!!fullContent.questionnaire.beforeAfter?.before?.length && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 10, fontWeight: 600, color: T.textVeryLow, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>
                      Before / After
                    </div>
                    <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
                      <div style={{ padding: "10px 12px", background: "var(--color-surface-subtle)", borderRadius: 8 }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: T.textMid, marginBottom: 6 }}>Before</div>
                        {fullContent.questionnaire.beforeAfter.before.map((item, i) => (
                          <div key={i} style={{ fontSize: 11, color: T.textLow, padding: "2px 0" }}>• {item}</div>
                        ))}
                      </div>
                      <div style={{ padding: "10px 12px", background: "var(--color-surface-subtle)", borderRadius: 8 }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: T.textMid, marginBottom: 6 }}>After</div>
                        {fullContent.questionnaire.beforeAfter.after.map((item, i) => (
                          <div key={i} style={{ fontSize: 11, color: T.textLow, padding: "2px 0" }}>• {item}</div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {!!fullContent.questionnaire.commit?.greeting && (
                  <div style={{ marginBottom: 16, padding: "10px 12px", background: "var(--color-surface-subtle)", borderRadius: 8 }}>
                    <div style={{ fontSize: 10, fontWeight: 600, color: T.textVeryLow, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>
                      Commit Screen
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: T.textMid, marginBottom: 4 }}>
                      {fullContent.questionnaire.commit.greeting}
                    </div>
                    <div style={{ fontSize: 11, color: T.textLow, marginBottom: 8 }}>
                      {fullContent.questionnaire.commit.subcopy}
                    </div>
                    {fullContent.questionnaire.commit.commitments?.map((item, i) => (
                      <div key={i} style={{ fontSize: 11, color: T.textLow, padding: "2px 0 2px 10px" }}>• {item}</div>
                    ))}
                  </div>
                )}

                {!!fullContent.questionnaire.reviews?.length && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 10, fontWeight: 600, color: T.textVeryLow, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>
                      Reviews
                    </div>
                    {fullContent.questionnaire.reviews.map((review, i) => (
                      <div key={i} style={{ marginBottom: 10, padding: "10px 12px", background: "var(--color-surface-subtle)", borderRadius: 8 }}>
                        <div style={{ fontSize: 11, color: "var(--color-warning)", marginBottom: 4 }}>
                          {renderStars(review.stars)}
                        </div>
                        <div style={{ fontSize: 11, color: T.textLow, marginBottom: 4 }}>
                          "{review.text}"
                        </div>
                        <div style={{ fontSize: 11, fontWeight: 600, color: T.textMid }}>
                          {review.name}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {fullContent.questionnaire.questions.map((q, i) => (
                  <div key={i} style={{ marginBottom: 12, padding: "10px 12px", background: "var(--color-surface-subtle)", borderRadius: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: T.textMid, marginBottom: 4 }}>
                      {i + 1}. {q.question}
                      <span style={{ fontWeight: 400, color: T.textVeryLow, marginLeft: 8, fontSize: 10, textTransform: "uppercase" }}>{q.type}</span>
                    </div>
                    {q.options?.map((opt, oi) => (
                      <div key={oi} style={{ fontSize: 11, color: T.textLow, padding: "2px 0 2px 10px" }}>· {opt.description}</div>
                    ))}
                    {q.placeholder && (
                      <div style={{ fontSize: 11, color: T.textVeryLow, fontStyle: "italic", marginTop: 2 }}>{q.placeholder}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </ReviewSection>

        </ReviewContainer>
      )}

      {/* ── Chat Editor (Step 2) ── */}
      {step === 2 && !isLaunching && !launchSucceeded && (
        <ChatSection>
          <div style={{ fontSize: 11, fontWeight: 600, color: T.textVeryLow, textTransform: "uppercase", letterSpacing: "0.7px" }}>
            Edit with AI
          </div>
          {chatMessages.length > 0 && (
            <ChatThread ref={chatThreadRef}>
              {chatMessages.map((m, i) => (
                <ChatBubble key={i} role={m.role}>{m.text}</ChatBubble>
              ))}
              {isChatLoading && (
                <ChatBubble role="assistant" style={{ color: T.textVeryLow, fontStyle: "italic" }}>
                  Updating…
                </ChatBubble>
              )}
            </ChatThread>
          )}
          <ChatInputRow>
            <ChatTextarea
              placeholder='e.g. "make the description more energetic" or "change primary color to navy blue"'
              value={chatInput}
              onChange={(e) => {
                setChatInput(e.target.value);
                e.target.style.height = "auto";
                e.target.style.height = e.target.scrollHeight + "px";
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleChatSend();
                }
              }}
              disabled={isChatLoading}
            />
            <ChatSendBtn
              type="button"
              disabled={!chatInput.trim() || isChatLoading}
              onClick={handleChatSend}
            >
              {isChatLoading ? "…" : "↑"}
            </ChatSendBtn>
          </ChatInputRow>
        </ChatSection>
      )}

      {/* ── CTA Button ── */}
      {step !== 1 && !isLaunching && !launchSucceeded && (
        <ButtonRow>
          {step === 2 && launchError && <ErrorBanner>{launchError}</ErrorBanner>}
          <Button
            disabled={!canProceed()}
            variant="contained"
            color="primary"
            fullWidth
            onClick={step === 2 ? handleLaunch : handleAnalyze}
            style={{ height: 48, fontSize: 15, fontWeight: 600, borderRadius: 10 }}
          >
            {step === 0
              ? genError
                ? "Try Again"
                : "Generate My Community"
              : launchError
              ? "Try Again"
              : "Launch My Community"}
          </Button>

          {step === 0 &&
            (hasSource ? (
              <CtaHint tone="ok">
                <CheckCircleIcon style={{ fontSize: 15, color: T.green }} />
                Ready. We'll turn this into a starter community in seconds.
              </CtaHint>
            ) : (
              <CtaHint tone="muted">
                Add your website, paste content, or upload a file to get started.
              </CtaHint>
            ))}

          {step === 2 && !launchError && (
            <CtaHint tone="muted">
              {draftTitle.trim()
                ? "You can edit everything later from your dashboard."
                : "Give your community a name to launch."}
            </CtaHint>
          )}
        </ButtonRow>
      )}
    </Wizard>
  );
};

export default OnboardingWizard;
